import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import { useAuth } from '../contexts/AuthProvider';
import { translateAuthError } from '../lib/authErrors';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const EMBER_BTN =
  'w-full inline-flex items-center justify-center gap-2 rounded-xl bg-ember px-4 py-3 font-semibold text-ink-950 shadow-[0_0_24px_-10px_#F26A4B] transition-all duration-200 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed';
const ERR_BOX =
  'rounded-xl bg-risk-high/10 border border-risk-high/30 text-risk-high text-sm p-3';

export default function Registar() {
  useDocumentMeta('Registar | Tutela', undefined, undefined, { noindex: true });
  const { signUp, configured } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('A palavra-passe deve ter pelo menos 8 caracteres.');
      return;
    }
    setLoading(true);
    const { error } = await signUp(email.trim(), password, fullName.trim() || undefined);
    setLoading(false);
    if (error) {
      setError(translateAuthError(error));
      return;
    }
    setDone(true);
  }

  if (done) {
    return (
      <AuthLayout
        title="Confirme o seu email"
        subtitle="Enviámos um link de confirmação para o seu email. Abra-o para ativar a conta."
        footer={
          <Link to="/entrar" className="text-ember hover:underline">
            Voltar a entrar
          </Link>
        }
      >
        <p className="text-sm text-mist-muted text-center">
          Se não receber o email em alguns minutos, verifique a pasta de spam.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Registar"
      subtitle="O registo é reservado a organizações aprovadas (organismos oficiais e parceiros)."
      footer={
        <>
          Já tem conta?{' '}
          <Link to="/entrar" className="text-ember hover:underline">
            Entrar
          </Link>
        </>
      }
    >
      {!configured && (
        <p className={`mb-4 ${ERR_BOX}`}>Autenticação indisponível nesta configuração.</p>
      )}
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label-dark" htmlFor="name">
            Nome
          </label>
          <input
            id="name"
            type="text"
            autoComplete="name"
            className="input-dark"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="O seu nome"
          />
        </div>
        <div>
          <label className="label-dark" htmlFor="email">
            Email profissional
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            className="input-dark"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="nome@organizacao.pt"
          />
        </div>
        <div>
          <label className="label-dark" htmlFor="password">
            Palavra-passe
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            className="input-dark"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mínimo 8 caracteres"
          />
        </div>
        {error && <p className={ERR_BOX}>{error}</p>}
        <button type="submit" disabled={loading || !configured} className={EMBER_BTN}>
          {loading ? 'A registar...' : 'Criar conta'}
        </button>
      </form>
    </AuthLayout>
  );
}
